import { useMemo } from 'react'
import type moment from 'moment'
import { useAppSelector } from 'hooks/reduxHooks'
import { isCharterEnabled } from 'selectors/featuresSelectors'
import { getOfferFlexibleCancellationTimelineContext } from './getOfferFlexibleCancellationTimelineContext'
import OfferPropertyFlexibleCancellationPoliciesView from './OfferPropertyFlexibleCancellationPoliciesView'

interface Props {
  cancellationPolicy: App.OfferCancellationPolicy
  propertyTimezoneOffset?: number
  /** Hotel check-in date. Always the real check-in; charter reference is passed separately. */
  checkInDate?: string | moment.Moment
  /**
   * Charter departure date when available. Leave undefined while still resolving so the shared
   * helper can hide rather than treating hotel check-in as departure.
   */
  charterDepartDate?: string | moment.Moment
  isFlashUSP?: boolean
}

/**
 * Redux-aware flexible cancellation entry for call sites that do not already have a timeline
 * context (e.g. offer list tiles). FlashCancellationPolicies should render the view directly.
 */
function OfferPropertyFlexibleCancellationPolicies(props: Props) {
  const {
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate,
    charterDepartDate,
    isFlashUSP,
  } = props
  const purchaseDate = useAppSelector(state => state.checkout.cart.existingOrder?.createdAt)
  const isCharterModeEnabled = useAppSelector(isCharterEnabled)

  const timelineContext = useMemo(() => getOfferFlexibleCancellationTimelineContext({
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate,
    charterDepartDate,
    purchaseDate,
    isCharterModeEnabled,
    availableForPurchase: true,
  }), [
    cancellationPolicy,
    charterDepartDate,
    checkInDate,
    isCharterModeEnabled,
    propertyTimezoneOffset,
    purchaseDate,
  ])

  if (!timelineContext) {
    return null
  }

  return (
    <OfferPropertyFlexibleCancellationPoliciesView
      details={timelineContext.details}
      isCharter={timelineContext.isCharter}
      isFlashUSP={isFlashUSP}
    />
  )
}

export default OfferPropertyFlexibleCancellationPolicies
